import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, X, Shield, Lock, Wallet, CheckCircle, KeyRound, UserCheck } from 'lucide-react'
import { useStore, type User } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import type { Locale } from '../i18n/translations'
import { api } from '../services/api'
import { useLegalContent } from '../hooks/useLegalContent'
import { isWeb3Mode } from '../lib/authMode'
import { ALLERGEN_INFO, DIETARY_INFO } from './StoreDetail/constants'
import { PROFILE_GATE_REDIRECT_KEY } from '../components/RequireAuth'
import ResidenceSelect from '../components/ResidenceSelect'
import { EMPTY_RESIDENCE, asksResidence, isCompleteResidence, type Residence } from '../lib/residence'

const NATIONALITIES = ['JP', 'US', 'TW', 'FR', 'KR', 'CN', 'OTHER'] as const
const LANGUAGES: Locale[] = ['ja', 'en', 'zh', 'ko', 'fr', 'de', 'es', 'th', 'vi', 'id']

// Default language based on nationality
const NATIONALITY_LANGUAGE_MAP: Record<string, Locale> = {
  JP: 'ja',
  US: 'en',
  TW: 'zh',
  FR: 'en',
  KR: 'en',
  CN: 'zh',
  OTHER: 'en',
}

const STEP_DURATION = 1200

interface WalletStep {
  key: string
  icon: typeof Lock
}

const WALLET_STEPS: WalletStep[] = [
  { key: 'wallet.step1', icon: Lock },
  { key: 'wallet.step2', icon: Shield },
  { key: 'wallet.step3', icon: Wallet },
  { key: 'wallet.step4', icon: CheckCircle },
]

const ACCOUNT_STEPS: WalletStep[] = [
  { key: 'account.step1', icon: UserCheck },
  { key: 'account.step2', icon: Shield },
  { key: 'account.step3', icon: CheckCircle },
]

export default function ProfileSetup() {
  const navigate = useNavigate()
  const { user, userId, wallet, createWallet, setDietaryPreferences } = useStore()
  const { t, locale, setLocale } = useI18n()
  const web3 = isWeb3Mode()
  const STEPS = web3 ? WALLET_STEPS : ACCOUNT_STEPS

  const [name, setName] = useState(user?.name || '')
  const [nickname, setNickname] = useState(user?.nickname || '')
  const [displayPreference, setDisplayPreference] = useState<'tag' | 'name' | 'nickname'>(
    user?.displayPreference || 'tag',
  )
  const [nationality, setNationality] = useState('')
  const [selectedLanguage, setSelectedLanguage] = useState<Locale>(locale)
  const [termsAgreed, setTermsAgreed] = useState(false)
  const [privacyAgreed, setPrivacyAgreed] = useState(false)
  const [allergens, setAllergens] = useState<string[]>([])
  const [dietaryStyles, setDietaryStyles] = useState<string[]>([])
  // #1207 紙レシート設定。既定は「不要」(アプリでいつでも見られる — 紙削減方針)
  const [wantsPaperReceipt, setWantsPaperReceipt] = useState(false)
  const [showDietarySection, setShowDietarySection] = useState(false)
  const [saving, setSaving] = useState(false)
  const [modalType, setModalType] = useState<'terms' | 'privacy' | null>(null)

  // Wallet animation state
  const [phase, setPhase] = useState<'form' | 'creating' | 'ready'>('form')
  const [currentStep, setCurrentStep] = useState(0)
  const [completedSteps, setCompletedSteps] = useState<number[]>([])
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // #2005 保存に失敗したことを画面に出す (握り潰すと未回答のまま離脱される)
  const [saveError, setSaveError] = useState(false)

  const handleNationalityChange = (value: string) => {
    setNationality(value)
    // Auto-set language based on nationality
    const defaultLang = NATIONALITY_LANGUAGE_MAP[value]
    if (defaultLang) {
      setSelectedLanguage(defaultLang)
      setLocale(defaultLang)
    }
  }

  // #2005 **自分で選んだときだけ保存する。** 国籍から自動で決まった言語
  // (NATIONALITY_LANGUAGE_MAP は FR/KR/OTHER を 'en' に落とす) まで記録すると、
  // フランスの人が English として数えられる — 直したかった歪みそのもの。
  // 選んでいなければ未回答のままにしておく。
  const [languageChosen, setLanguageChosen] = useState(false)

  const handleLanguageChange = (value: Locale) => {
    setSelectedLanguage(value)
    setLanguageChosen(true)
    setLocale(value)
  }

  // #2004 居住地 (任意)。国籍に日本を選んだ人にだけ聞く。県と市が揃ったときだけ送る —
  // 県だけ選んで進んだ人は「未回答」のまま (登録は止めない)
  const [residence, setResidence] = useState<Residence>(EMPTY_RESIDENCE)
  const sendsResidence = asksResidence(nationality) && isCompleteResidence(residence)

  const isValid = name.trim() && nationality && termsAgreed && privacyAgreed

  const handleSubmit = async () => {
    if (!isValid || saving) return
    setSaveError(false)
    setSaving(true)

    try {
      // Update profile via API if available
      const trimmedNickname = nickname.trim()
      // If user picked "nickname" display but didn't enter one, fall back to tag.
      let residenceSaved = false
      const finalPref: 'tag' | 'name' | 'nickname' =
        displayPreference === 'nickname' && !trimmedNickname ? 'tag' : displayPreference
      if (userId) {
        // #2005 **保存の失敗を握り潰さない。** 以前は .catch(() => {}) で捨てていた
        // ので、サーバに何も残らないまま「登録できた」画面に進み、国籍が登録時の
        // 既定値のまま残っていた。失敗は投げて、下の catch で画面に出す。
        await api.updateProfile(userId, {
          name: name.trim(),
          nationality,
          // #2005 自分で選んだときだけ送る (自動で決まった値は未回答のまま)
          language: languageChosen ? selectedLanguage : undefined,
          nickname: trimmedNickname || undefined,
          displayPreference: finalPref,
          wantsPaperReceipt,
        })
        // #2004 居住地は**必須項目とは別に**送る。任意の項目が断られた (マスタの作り直しで
        // 選択肢が古くなっていた等) せいで、お名前と国籍ごと登録が止まってはいけない。
        // 失敗しても登録は進める — あとでプロフィールから入れられる。
        if (sendsResidence) {
          try {
            await api.updateProfile(userId, {
              residencePrefecture: residence.prefecture, residenceCity: residence.city,
            })
            residenceSaved = true
          } catch (e) {
            console.error('[ProfileSetup] residence not saved (registration continues)', e)
          }
        }
      }

      // Update local store
      useStore.setState((state) => ({
        user: state.user
          ? {
              ...state.user,
              name: name.trim(),
              nationality: nationality as User['nationality'],
              nickname: trimmedNickname || undefined,
              displayPreference: finalPref,
              wantsPaperReceipt,
              ...(residenceSaved
                ? { residencePrefecture: residence.prefecture, residenceCity: residence.city }
                : {}),
            }
          : state.user,
      }))

      // Save dietary preferences
      if (allergens.length > 0 || dietaryStyles.length > 0) {
        setDietaryPreferences(allergens, dietaryStyles)
      }
      // Mark onboarding as shown since user made a choice
      localStorage.setItem('dietaryOnboardingShown', 'true')

      // Mark setup as complete
      localStorage.setItem('hasCompletedSetup', 'true')

      // Start wallet creation animation
      setPhase('creating')
      setCurrentStep(0)
      setCompletedSteps([])
    } catch {
      // #2005 **黙って戻さない。** 何も言わずに元の画面に戻ると、利用者は
      // 何が起きたか分からず、そのまま離脱して国籍が未回答のまま残る。
      setSaveError(true)
      setSaving(false)
    }
  }

  // Wallet step progression
  useEffect(() => {
    if (phase !== 'creating') return

    if (currentStep >= STEPS.length) {
      timerRef.current = setTimeout(() => {
        if (web3) createWallet()
        setPhase('ready')
      }, 600)
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current)
      }
    }

    timerRef.current = setTimeout(() => {
      setCompletedSteps((prev) => [...prev, currentStep])
      setCurrentStep((prev) => prev + 1)
    }, STEP_DURATION)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [phase, currentStep, createWallet, STEPS.length, web3])

  const [confettiParticles] = useState(() =>
    Array.from({ length: 20 }).map(() => ({
      left: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 2 + Math.random() * 2,
    }))
  )

  const handleContinue = useCallback(() => {
    // 未回答で戻されてきた人は、元いた場所へ返す (会場で QR を読もうとしていた人を
    // /home に落とすと、もう一度たどり直すことになる)
    const back = sessionStorage.getItem(PROFILE_GATE_REDIRECT_KEY)
    if (back) {
      sessionStorage.removeItem(PROFILE_GATE_REDIRECT_KEY)
      navigate(back, { replace: true })
      return
    }
    // ログインの前に覚えた戻り先。Login は**既存の人だけ**これを使い、新規の人はここ
    // (プロフィール登録) へ送るので、ここで使わないと捨てられる。スタンプ帳の
    // 「ログインして合言葉を入れる」(#2490) や「アカウントにする」から初めて登録した人が、
    // ホームに落ちてスタンプ帳をたどり直すことになっていた
    const intended = sessionStorage.getItem('postLoginRedirect')
    if (intended) {
      sessionStorage.removeItem('postLoginRedirect')
      navigate(intended, { replace: true })
      return
    }
    navigate('/home', { replace: true })
  }, [navigate])

  const openModal = (type: 'terms' | 'privacy') => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setModalType(type)
  }

  // 本文は開く時にだけ読み込む (メインの JS に入れない — hooks/useLegalContent.ts)
  const legalContent = useLegalContent(locale, !!modalType)

  const walletAddress = wallet?.address
    ? `${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}`
    : '0x1a2b...1234'

  const securityPoints = [
    { key: 'wallet.security1', icon: Lock },
    { key: 'wallet.security2', icon: KeyRound },
    { key: 'wallet.security3', icon: Shield },
  ]

  // Form phase
  if (phase === 'form') {
    return (
      <div className="flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
        {/* Header */}
        <div className="flex flex-col items-center px-6 pt-12 pb-6">
          <h1 className="animate-scale-in font-display text-3xl font-bold tracking-tight">
            <span className="text-navy">YOKOSO</span>{' '}
            <span className="text-yokoso-red">PAY</span>
          </h1>

          <h2
            className="animate-fade-in mt-8 font-display text-[22px] font-bold text-text-primary text-center"
            style={{ animationDelay: '0.15s', animationFillMode: 'both' }}
          >
            {t('setup.title')}
          </h2>

          <p
            className="animate-fade-in mt-2 font-body text-[14px] text-text-secondary text-center max-w-[300px] leading-relaxed"
            style={{ animationDelay: '0.25s', animationFillMode: 'both' }}
          >
            {t('setup.subtitle')}
          </p>
        </div>

        {/* Form */}
        <div
          className="animate-fade-in flex flex-col gap-5 px-6"
          style={{ animationDelay: '0.35s', animationFillMode: 'both' }}
        >
          {/* Name */}
          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-text-primary">
              {t('setup.name')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('setup.namePlaceholder')}
              className="h-[48px] w-full rounded-xl border border-[#E5E5E5] bg-white px-4 font-body text-[15px] text-text-primary outline-none transition-colors focus:border-navy focus:ring-2 focus:ring-navy/10"
            />
          </div>

          {/* Nickname (optional, used for self-order display) */}
          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-text-primary">
              {t('profile.nicknameLabel')}{' '}
              <span className="font-body text-xs text-text-tertiary">{t('setup.dietaryOptional').split(' - ')[0]}</span>
            </label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder={t('profile.nicknamePlaceholder')}
              maxLength={24}
              className="h-[48px] w-full rounded-xl border border-[#E5E5E5] bg-white px-4 font-body text-[15px] text-text-primary outline-none transition-colors focus:border-navy focus:ring-2 focus:ring-navy/10"
            />
            <p className="mt-1 font-body text-[11px] text-text-tertiary leading-relaxed">
              {t('profile.nicknameHelp')}
            </p>
          </div>

          {/* Display preference (self-order) */}
          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-text-primary">
              {t('profile.displayPreferenceLabel')}
            </label>
            <div className="space-y-2">
              {(['tag', 'name', 'nickname'] as const).map((opt) => {
                const disabled = opt === 'nickname' && !nickname.trim()
                const label =
                  opt === 'tag'
                    ? t('profile.displayPref.tag')
                    : opt === 'name'
                      ? t('profile.displayPref.name', { name: name.trim() || t('setup.namePlaceholder') })
                      : t('profile.displayPref.nickname', { nickname: nickname.trim() || '...' })
                return (
                  <label
                    key={opt}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${
                      displayPreference === opt
                        ? 'border-navy bg-navy/5'
                        : 'border-[#E5E5E5] bg-white'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="radio"
                      name="displayPreference"
                      value={opt}
                      checked={displayPreference === opt}
                      disabled={disabled}
                      onChange={() => !disabled && setDisplayPreference(opt)}
                      className="h-4 w-4 text-navy accent-navy"
                    />
                    <span className="font-body text-[13px] text-text-primary">{label}</span>
                  </label>
                )
              })}
              {!nickname.trim() && (
                <p className="font-body text-[11px] text-text-tertiary">
                  {t('profile.displayPrefNicknameRequired')}
                </p>
              )}
              <p className="font-body text-[11px] text-text-tertiary leading-relaxed">
                {t('profile.displayPrefNote')}
              </p>
            </div>
          </div>

          {/* Nationality */}
          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-text-primary">
              {t('setup.nationality')}
            </label>
            <div className="relative">
              <select
                value={nationality}
                onChange={(e) => handleNationalityChange(e.target.value)}
                className="h-[48px] w-full appearance-none rounded-xl border border-[#E5E5E5] bg-white px-4 pr-10 font-body text-[15px] text-text-primary outline-none transition-colors focus:border-navy focus:ring-2 focus:ring-navy/10"
              >
                <option value="" disabled>
                  {t('setup.selectNationality')}
                </option>
                {NATIONALITIES.map((code) => (
                  <option key={code} value={code}>
                    {t(`setup.nationality.${code}`)}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={18}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary"
              />
            </div>
          </div>

          {/* #2004 居住地 (任意)。必須項目 (お名前・国籍) の後ろに置き、既定は未選択。
            * 訪日客に日本の居住地を聞いても意味が無いので、国籍に日本を選んだ人にだけ出す */}
          {asksResidence(nationality) && (
            <div>
              <p className="mb-0.5 block font-body text-sm font-medium text-text-primary">
                {t('residence.title')}
              </p>
              <p className="mb-1.5 font-body text-xs text-text-tertiary leading-relaxed">
                {t('residence.help')}
              </p>
              <ResidenceSelect
                value={residence}
                onChange={setResidence}
                disabled={saving}
                selectClassName="mt-1 h-[48px] w-full rounded-xl border border-[#E5E5E5] bg-white px-3 font-body text-[15px] text-text-primary outline-none transition-colors focus:border-navy focus:ring-2 focus:ring-navy/10 disabled:bg-gray-50 disabled:text-text-tertiary"
              />
            </div>
          )}

          {/* Language */}
          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-text-primary">
              {t('setup.language')}
            </label>
            <div className="relative">
              <select
                value={selectedLanguage}
                onChange={(e) => handleLanguageChange(e.target.value as Locale)}
                className="h-[48px] w-full appearance-none rounded-xl border border-[#E5E5E5] bg-white px-4 pr-10 font-body text-[15px] text-text-primary outline-none transition-colors focus:border-navy focus:ring-2 focus:ring-navy/10"
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang} value={lang}>
                    {t(`setup.language.${lang}`)}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={18}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary"
              />
            </div>
          </div>

          {/* Dietary Preferences (optional) */}
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setShowDietarySection(!showDietarySection)}
              className="flex w-full items-center justify-between py-2 text-left"
            >
              <div>
                <p className="font-body text-sm font-medium text-text-primary">
                  🍽️ {t('setup.dietaryTitle')}
                </p>
                <p className="font-body text-xs text-text-tertiary mt-0.5">
                  {allergens.length + dietaryStyles.length > 0
                    ? t('setup.dietarySelected', { count: allergens.length + dietaryStyles.length })
                    : t('setup.dietaryOptional')}
                </p>
              </div>
              <ChevronDown size={16} className={`text-text-tertiary transition-transform ${showDietarySection ? 'rotate-180' : ''}`} />
            </button>
            {showDietarySection && (
              <div className="mt-2 rounded-xl bg-gray-50 p-3 space-y-3 animate-fade-in">
                <div>
                  <p className="font-body text-xs font-medium text-text-primary mb-1.5">
                    {t('profile.dietaryAllergens')}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(ALLERGEN_INFO).map(([key, info]) => {
                      const active = allergens.includes(key)
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setAllergens(active ? allergens.filter(a => a !== key) : [...allergens, key])}
                          className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors ${
                            active ? 'bg-red-500 text-white' : 'bg-white text-text-secondary border border-gray-200'
                          }`}
                        >
                          <span>{info.emoji}</span>
                          <span>{t(info.label)}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
                <div>
                  <p className="font-body text-xs font-medium text-text-primary mb-1.5">
                    {t('profile.dietaryStyles')}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(DIETARY_INFO).map(([key, info]) => {
                      const active = dietaryStyles.includes(key)
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setDietaryStyles(active ? dietaryStyles.filter(d => d !== key) : [...dietaryStyles, key])}
                          className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors ${
                            active ? 'bg-emerald-500 text-white' : 'bg-white text-text-secondary border border-gray-200'
                          }`}
                        >
                          <span>{info.emoji}</span>
                          <span>{t(info.label)}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
                <p className="font-body text-[10px] text-text-tertiary leading-relaxed">
                  {t('profile.dietaryDisclaimer')}
                </p>
              </div>
            )}
          </div>

          {/* #1207 紙レシート設定 — アカウント作成時に説明つきで選択させる (サーバ保存
              なのでブラウザのデータ削除でも消えない)。あとから Profile 設定で変更可。
              効くのはアプリ決済時の店頭自動印刷のみ (店頭 S700/現金はアカウント非連携)。 */}
          <div className="mt-2 rounded-xl border border-navy/10 bg-navy/5 px-4 py-3">
            <p className="font-body text-xs font-semibold text-text-primary">
              🧾 {t('receiptPref.title')}
            </p>
            <p className="mt-1 font-body text-[11px] leading-relaxed text-text-tertiary">
              {t('receiptPref.hint')}
            </p>
            <div className="mt-2 space-y-1.5">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="paper-receipt"
                  checked={!wantsPaperReceipt}
                  onChange={() => setWantsPaperReceipt(false)}
                  className="mt-0.5 accent-navy"
                />
                <span className="font-body text-xs text-text-secondary">
                  {t('receiptPref.no')}
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="paper-receipt"
                  checked={wantsPaperReceipt}
                  onChange={() => setWantsPaperReceipt(true)}
                  className="mt-0.5 accent-navy"
                />
                <span className="font-body text-xs text-text-secondary">
                  {t('receiptPref.yes')}
                </span>
              </label>
            </div>
          </div>

          {/* Terms & Privacy */}
          <div className="mt-2 flex flex-col gap-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={termsAgreed}
                onChange={(e) => setTermsAgreed(e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-navy accent-navy"
              />
              <span className="font-body text-[13px] leading-relaxed text-text-secondary">
                {t('setup.termsAgree', {
                  terms: `<a>${t('setup.terms')}</a>`,
                })
                  .split(/(<a>.*?<\/a>)/)
                  .map((part, i) => {
                    const match = part.match(/^<a>(.*)<\/a>$/)
                    if (match)
                      return (
                        <span
                          key={i}
                          className="text-navy underline underline-offset-2 cursor-pointer"
                          onClick={openModal('terms')}
                        >
                          {match[1]}
                        </span>
                      )
                    return part
                  })}
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={privacyAgreed}
                onChange={(e) => setPrivacyAgreed(e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-navy accent-navy"
              />
              <span className="font-body text-[13px] leading-relaxed text-text-secondary">
                {t('setup.privacyAgree', {
                  privacy: `<a>${t('setup.privacy')}</a>`,
                })
                  .split(/(<a>.*?<\/a>)/)
                  .map((part, i) => {
                    const match = part.match(/^<a>(.*)<\/a>$/)
                    if (match)
                      return (
                        <span
                          key={i}
                          className="text-navy underline underline-offset-2 cursor-pointer"
                          onClick={openModal('privacy')}
                        >
                          {match[1]}
                        </span>
                      )
                    return part
                  })}
              </span>
            </label>
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Submit Button */}
        <div
          className="animate-fade-in px-6 pb-8"
          style={{ animationDelay: '0.45s', animationFillMode: 'both' }}
        >
          {/* #2005 保存に失敗したことを出す。黙って戻すと、何が起きたか分からないまま
              離脱され、国籍が未回答のまま残る */}
          {saveError && (
            <p
              role="alert"
              className="mb-3 rounded-xl bg-red-50 px-4 py-3 font-body text-sm text-red-800"
            >
              {t('setup.saveFailed')}
            </p>
          )}
          <button
            onClick={handleSubmit}
            disabled={!isValid || saving}
            className={`tap-effect flex h-[54px] w-full items-center justify-center rounded-xl font-display text-[16px] font-bold transition-all duration-200 ${
              isValid && !saving
                ? 'bg-navy text-white hover:bg-navy-dark active:scale-[0.97]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? '...' : t('setup.continue')}
          </button>
        </div>

        {/* Legal Document Modal */}
        {modalType && (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
            onClick={() => setModalType(null)}
          >
            <div
              className="animate-slide-up w-full max-w-[430px] rounded-t-2xl bg-white shadow-xl"
              style={{ maxHeight: '85vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <h3 className="font-display text-[16px] font-bold text-text-primary">
                  {modalType === 'terms' ? t('setup.terms') : t('setup.privacy')}
                </h3>
                <button
                  onClick={() => setModalType(null)}
                  className="tap-effect flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X size={18} className="text-text-secondary" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="overflow-y-auto px-5 py-4" style={{ maxHeight: 'calc(85vh - 64px)' }}>
                <pre className="whitespace-pre-wrap font-body text-[13px] leading-relaxed text-text-secondary">
                  {(modalType === 'terms' ? legalContent?.terms : legalContent?.privacy)
                    ?? t('common.loading')}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Wallet creation animation phase
  if (phase === 'creating') {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center"
        style={{ background: 'linear-gradient(160deg, #0D1B2A 0%, #1D3557 50%, #457B9D 100%)' }}
      >
        {/* Decorative background glow */}
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full opacity-20 blur-[80px]"
          style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }}
        />

        <div className="animate-fade-in relative z-10 flex flex-col items-center px-8 w-full max-w-[380px]">
          {/* Spinning shield icon */}
          <div className="relative mb-8">
            <div className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.2), rgba(212,175,55,0.05))' }}
            >
              <Shield size={36} className="text-gold animate-pulse" />
            </div>
            <div className="absolute inset-[-8px] rounded-full border-2 border-gold/30 animate-[spin_3s_linear_infinite]" />
            <div className="absolute inset-[-8px] rounded-full border-2 border-transparent border-t-gold/60 animate-[spin_2s_linear_infinite]" />
          </div>

          {/* Title */}
          <h2 className="font-display text-[22px] font-bold text-white mb-2 text-center">
            {t(web3 ? 'wallet.creating' : 'account.creating')}
          </h2>
          {web3 && (
            <p className="font-body text-[13px] text-white/50 mb-10 text-center">
              Web3Auth MPC-TSS Protocol
            </p>
          )}
          {!web3 && <div className="mb-10" />}

          {/* Steps */}
          <div className="w-full space-y-4 mb-12">
            {STEPS.map((step, i) => {
              const isCompleted = completedSteps.includes(i)
              const isActive = currentStep === i && !isCompleted
              const isPending = !isCompleted && !isActive
              const StepIcon = step.icon

              return (
                <div
                  key={i}
                  className={`flex items-center gap-4 rounded-xl px-4 py-3 transition-all duration-500 ${
                    isActive
                      ? 'bg-white/10 wallet-step-pulse'
                      : isCompleted
                        ? 'bg-white/5'
                        : 'bg-transparent'
                  }`}
                  style={{
                    opacity: isPending ? 0.35 : 1,
                    transform: isActive ? 'scale(1.02)' : 'scale(1)',
                  }}
                >
                  <div className={`flex h-10 w-10 items-center justify-center rounded-full shrink-0 transition-all duration-500 ${
                    isCompleted
                      ? 'bg-matcha/20'
                      : isActive
                        ? 'bg-gold/20'
                        : 'bg-white/5'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle size={20} className="text-matcha animate-scale-in" />
                    ) : (
                      <StepIcon size={20} className={`transition-colors duration-300 ${
                        isActive ? 'text-gold' : 'text-white/30'
                      }`} />
                    )}
                  </div>

                  <span className={`font-body text-[14px] transition-colors duration-300 ${
                    isCompleted
                      ? 'text-matcha font-medium'
                      : isActive
                        ? 'text-white font-medium'
                        : 'text-white/30'
                  }`}>
                    {t(step.key)}
                  </span>

                  {isActive && (
                    <div className="ml-auto flex gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-gold animate-[pulse_1s_ease-in-out_infinite]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-gold animate-[pulse_1s_ease-in-out_0.2s_infinite]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-gold animate-[pulse_1s_ease-in-out_0.4s_infinite]" />
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Progress bar */}
          <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden mb-8">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${((completedSteps.length) / STEPS.length) * 100}%`,
                background: 'linear-gradient(90deg, #D4AF37, #F4D03F)',
              }}
            />
          </div>

          {/* Powered by badge */}
          <div className="flex items-center gap-2 opacity-40">
            <Shield size={14} className="text-white" />
            <span className="font-body text-[11px] text-white tracking-wider uppercase">
              {t('wallet.poweredBy')}
            </span>
          </div>
        </div>
      </div>
    )
  }

  // Wallet ready phase
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center"
      style={{ background: 'linear-gradient(160deg, #0D1B2A 0%, #1D3557 50%, #457B9D 100%)' }}
    >
      {/* Confetti particles */}
      {confettiParticles.map((particle, i) => (
        <div
          key={i}
          className="wallet-confetti absolute w-2 h-2 rounded-full"
          style={{
            left: `${particle.left}%`,
            top: `-5%`,
            background: ['#D4AF37', '#F4D03F', '#7BA05B', '#FFB5C2', '#E63946', '#457B9D'][i % 6],
            animationDelay: `${particle.delay}s`,
            animationDuration: `${particle.duration}s`,
          }}
        />
      ))}

      <div className="animate-scale-in relative z-10 flex flex-col items-center px-8 w-full max-w-[380px]">
        {/* Success checkmark */}
        <div className="relative mb-6">
          <div className="w-24 h-24 rounded-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgba(123,160,91,0.3), rgba(123,160,91,0.1))' }}
          >
            <CheckCircle size={48} className="text-matcha" />
          </div>
          <div className="absolute inset-0 rounded-full border-2 border-matcha/30 animate-[pulse-ring_2s_ease-out_infinite]" />
          <div className="absolute inset-[-6px] rounded-full border border-matcha/15 animate-[pulse-ring_2s_ease-out_0.5s_infinite]" />
        </div>

        {/* Title */}
        <h2 className="font-display text-[24px] font-bold text-white mb-8 text-center">
          {t(web3 ? 'wallet.ready' : 'account.ready')}
        </h2>

        {web3 ? (
          <>
            {/* Wallet address card */}
            <div className="w-full rounded-2xl p-5 mb-8"
              style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.04))', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <p className="font-body text-[12px] text-white/50 uppercase tracking-wider mb-2">
                {t('wallet.address')}
              </p>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/20 shrink-0">
                  <Wallet size={20} className="text-gold" />
                </div>
                <span className="font-mono text-[15px] text-white tracking-wide">
                  {walletAddress}
                </span>
              </div>
            </div>

            {/* Security assurance points */}
            <div className="w-full space-y-3 mb-10">
              {securityPoints.map((point, i) => {
                const PointIcon = point.icon
                return (
                  <div
                    key={i}
                    className="animate-fade-in flex items-center gap-3"
                    style={{ animationDelay: `${0.2 + i * 0.15}s`, animationFillMode: 'both' }}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-matcha/15 shrink-0">
                      <PointIcon size={16} className="text-matcha" />
                    </div>
                    <span className="font-body text-[13px] text-white/80">
                      {t(point.key)}
                    </span>
                  </div>
                )
              })}
            </div>

            {/* Powered by badge */}
            <div className="flex items-center gap-2 opacity-40 mb-8">
              <Shield size={14} className="text-white" />
              <span className="font-body text-[11px] text-white tracking-wider uppercase">
                {t('wallet.poweredBy')}
              </span>
            </div>
          </>
        ) : (
          <p className="font-body text-[14px] text-white/70 text-center mb-10">
            {t('account.readyDesc')}
          </p>
        )}

        {/* CTA Button */}
        <button
          onClick={handleContinue}
          className="tap-effect w-full h-[54px] rounded-xl font-display text-[16px] font-bold text-navy-dark tracking-wide transition-all duration-200 hover:brightness-110 active:scale-[0.97]"
          style={{ background: 'linear-gradient(135deg, #D4AF37, #F4D03F)' }}
        >
          {t('wallet.continue')}
        </button>
      </div>
    </div>
  )
}
